import React, { createContext, useContext } from 'react';
import CADIAnalyticsProvider, { useCADIAnalytics } from '../contexts/CADI-analytics';
import type { AnalyticsWrapperProps, ClickInfo, FileInfo, TransactionInfo, UnifiedAnalytics } from './AnalyticsWrapperTypes';

const UnifiedAnalyticsContext = createContext<UnifiedAnalytics | null>(null);

const UnifiedAnalyticsProvider: React.FC<AnalyticsWrapperProps> = ({ children }) => {
    const cadiAnalytics = useCADIAnalytics();

    const unifiedAnalytics: UnifiedAnalytics = {
        trackAnalyticsClickEvent: (eventName, destinationUrl, region, product, id, additionalData, action, newEventName, featureName) => {
            const clickInfo: ClickInfo = {
                url: destinationUrl,
                region: region,
                type: 'button',
                action: action,
                productInfo: {
                    productName: product,
                    id: id
                }
            };
            cadiAnalytics.trackAnalyticsClickEvent(newEventName, featureName, clickInfo, undefined, {}, undefined);
        },
        trackAnalyticsPageEvent: (eventName, pageInfo, featureName, newEventName) => {
            cadiAnalytics.trackAnalyticsPageEvent(newEventName, featureName);
        },
        trackAnalyticsClickTransactionEvent: (action, product, transactionType, targetedProduct, newEventName, featureName = 'Transaction Feature', productTerm) => {
            const clickInfo: ClickInfo = {
                url: '',
                region: '',
                type: 'button',
                action: action,
                productInfo: {
                    productName: product,
                    id: '',
                    productTerm: productTerm
                }
            };

            const transactionInfo: TransactionInfo = {
                transactionType: transactionType,
                transactionStage: action,
                currency: 'GBP'
            };

            cadiAnalytics.trackAnalyticsClickEvent(
                newEventName,
                featureName,
                clickInfo,
                undefined,
                transactionInfo,
                undefined
            );
        },
        trackAnalyticsPopupClick: (flowName, action = 'click', name, featureName, newEventName) => {
            const clickInfo: ClickInfo = {
                url: document.location.href,
                region: 'popup',
                type: 'popup',
                action: action,
                productInfo: {
                    productName: '',
                    id: ''
                }
            };
            cadiAnalytics.trackAnalyticsClickEvent(
                newEventName,
                featureName,
                clickInfo,
                undefined,
                undefined,
                undefined
            );
        },
        trackAnalyticsPopupEvent: (flowName, id, featureName, newEventName, roadblockName) => {
            const roadblockInfo = roadblockName ? { roadblockName: roadblockName } : undefined;
            cadiAnalytics.trackAnalyticsClickEvent(
                newEventName,
                featureName,
                undefined,
                undefined,
                undefined,
                roadblockInfo
            );
        },
        trackAnalyticsDownloadEvent: (eventName, clickInfo, fileInfo, newEventName) => {
            const clickInfo2: ClickInfo = {
                url: clickInfo.url,
                region: clickInfo.region,
                type: clickInfo.type,
                action: 'download',
                productInfo: {
                    productName: '',
                    id: ''
                }
            };

            const fileInfo2: FileInfo = {
                fileName: fileInfo.fileName,
                fileDescription: fileInfo.fileDescription
            };

            cadiAnalytics.trackAnalyticsClickEvent(
                newEventName,
                'download',
                clickInfo2,
                fileInfo2,
                undefined,
                undefined
            );
        },
        trackAnalyticsOtpEvent: (engagement, region, action, product = 'digital savings', eventName) => {
            const clickInfo: ClickInfo = {
                url: '',
                region: region,
                type: '',
                action: action,
                productInfo: {
                    productName: product,
                    id: 'otp'
                }
            };
            cadiAnalytics.trackAnalyticsClickEvent(
                eventName,
                'OTP',
                clickInfo,
                undefined,
                undefined,
                undefined
            );
        },
        trackAnalyticsIntegrationEvent: (eventName, featureName, action, product = 'digital savings', id = 'integration-event') => {
            const clickInfo: ClickInfo = {
                url: window.location.href,
                region: 'integration',
                type: 'integration',
                action: action,
                productInfo: {
                    productName: product,
                    id: id
                }
            };

            cadiAnalytics.trackAnalyticsClickEvent(
                eventName,
                featureName,
                clickInfo,
                undefined,
                undefined,
                undefined
            );
        },
        trackPopupViewedEvent: (id, name, eventName, roadblockName, featureName = 'LinkedAccount') => {
            const clickInfo: ClickInfo = {
                url: '',
                region: '',
                type: 'Popup',
                action: 'viewed',
                productInfo: {
                    productName: '',
                    id: ''
                }
            };

            const roadblockInfo = roadblockName ? { roadblockName: roadblockName } : undefined;

            cadiAnalytics.trackAnalyticsClickEvent(
                eventName,
                featureName,
                clickInfo,
                undefined,
                undefined,
                roadblockInfo
            );
        },
        trackPopupClickedEvent: (id, name, textClick, eventName, roadblockName, featureName = 'LinkedAccount') => {
            const clickInfo: ClickInfo = {
                url: '',
                region: '',
                type: 'Popup',
                action: 'viewed',
                productInfo: {
                    productName: '',
                    id: ''
                }
            };

            const roadblockInfo = roadblockName ? { roadblockName: roadblockName } : undefined;

            cadiAnalytics.trackAnalyticsClickEvent(
                eventName,
                featureName,
                clickInfo,
                undefined,
                undefined,
                roadblockInfo
            );
        },
        trackPopupClosedEvent: (id, name, eventName, roadblockName, featureName = 'LinkedAccount') => {
            const clickInfo: ClickInfo = {
                url: '',
                region: '',
                type: 'Popup',
                action: 'viewed',
                productInfo: {
                    productName: '',
                    id: ''
                }
            };

            const roadblockInfo = roadblockName ? { roadblockName: roadblockName } : undefined;

            cadiAnalytics.trackAnalyticsClickEvent(
                eventName,
                featureName,
                clickInfo,
                undefined,
                undefined,
                roadblockInfo
            );
        }
    };

    return (
        <UnifiedAnalyticsContext.Provider value={unifiedAnalytics}>
            {children}
        </UnifiedAnalyticsContext.Provider>
    );
};

const AnalyticsWrapper: React.FC<AnalyticsWrapperProps> = ({ children }) => (
    <CADIAnalyticsProvider>
        <UnifiedAnalyticsProvider>
            {children}
        </UnifiedAnalyticsProvider>
    </CADIAnalyticsProvider>
);

/**
 * Custom hook to use unified analytics
 */
export const useUnifiedAnalytics = (): UnifiedAnalytics => {
    const context = useContext(UnifiedAnalyticsContext);
    if (!context) {
        throw new Error('useUnifiedAnalytics must be used within an AnalyticsWrapper');
    }
    return context;
};

export default AnalyticsWrapper;
